import { isValidKey } from './names';

// .env files read as dotenv reads them (what NestJS' ConfigModule, Nuxt and Vite use underneath), so a pasted file
// means what the apps would read; and written so that dotenv reads every value back exactly. Where dotenv would
// quietly read something else than was meant (an unclosed quote, text after the closing quote), this refuses the line
// instead: in a vault a wrong value is worse than a missing one.

export interface DotenvEntry {
    key: string;
    value: string;
    // The line the entry starts on, from 1.
    line: number;
}

export interface DotenvIssue {
    line: number;
    message: string;
}

export interface DotenvParseResult {
    // One per key, in the order the keys first appear; a key written twice keeps its last value, as in dotenv.
    entries: DotenvEntry[];
    // Keys written more than once.
    duplicates: string[];
    // Lines that hold nothing the vault can keep.
    errors: DotenvIssue[];
    // Entries kept, but maybe not as meant.
    warnings: DotenvIssue[];
}

// dotenv's own line: an optional `export`, the key, then `=` or a colon and a space.
const ENTRY = /^[ \t]*(?:export[ \t]+)?([\w.-]+)(?:[ \t]*=|:(?=[ \t]))/;
const QUOTES = new Set(['"', "'", '`']);
const BLANK_OR_COMMENT = /^[ \t]*(?:#.*)?$/;

export function parseDotenv(source: string): DotenvParseResult {
    const text = source.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
    const entries = new Map<string, DotenvEntry>();
    const duplicates = new Set<string>();
    const errors: DotenvIssue[] = [];
    const warnings: DotenvIssue[] = [];
    let pos = 0;
    let line = 1;

    while (pos < text.length) {
        const lineEnd = endOfLine(text, pos);
        const current = text.slice(pos, lineEnd);
        const match = BLANK_OR_COMMENT.test(current) ? null : ENTRY.exec(current);
        if (!match) {
            if (!BLANK_OR_COMMENT.test(current)) {
                errors.push({ line, message: current.includes('=') ? 'The name before = is not a valid key' : 'Not a KEY=value line' });
            }
            pos = lineEnd + 1;
            line += 1;
            continue;
        }

        const key = match[1] as string;
        const valueStart = pos + match[0].length;
        let cursor = valueStart;
        while (text[cursor] === ' ' || text[cursor] === '\t') {
            cursor += 1;
        }
        const quote = text[cursor] ?? '';
        let value: string;
        let next: number;
        if (QUOTES.has(quote)) {
            const close = closingQuote(text, cursor + 1, quote);
            // dotenv's last resort: a line whose value starts and ends with the same quote, quotes inside it included.
            const wrapped = close === null ? unquotedPart(text.slice(cursor, lineEnd)) : null;
            if (close === null && !(wrapped && wrapped.length >= 2 && wrapped.endsWith(quote))) {
                errors.push({ line, message: `The ${quoteName(quote)} quote is never closed, or text follows the closing one` });
                pos = lineEnd + 1;
                line += 1;
                continue;
            }
            value = close === null ? (wrapped as string).slice(1, -1) : text.slice(cursor + 1, close);
            if (quote === '"') {
                value = value.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
            }
            next = close === null ? lineEnd + 1 : endOfLine(text, close) + 1;
        } else {
            const raw = text.slice(cursor, lineEnd);
            const hash = raw.indexOf('#');
            value = unquotedPart(raw);
            const glued = hash > 0 ? !/\s/.test(raw[hash - 1] as string) : hash === 0 && cursor === valueStart && raw.length > 1;
            if (glued) {
                warnings.push({ line, message: `${key} stops at the #: quote the value if the # belongs to it` });
            }
            next = lineEnd + 1;
        }

        if (!isValidKey(key)) {
            errors.push({ line, message: `${key} is not a valid key: letters, digits and _, not starting with a digit` });
        } else {
            const previous = entries.get(key);
            if (previous) {
                duplicates.add(key);
                previous.value = value;
                previous.line = line;
            } else {
                entries.set(key, { key, value, line });
            }
        }
        line += countLines(text, pos, next);
        pos = next;
    }

    return { entries: [...entries.values()], duplicates: [...duplicates], errors, warnings };
}

// One entry as a line dotenv reads back exactly: bare when the value is plain, quoted otherwise. `exact` is false only
// for a value no quoting can carry through dotenv (one holding all three quote characters and a newline, or `\n`
// written out), written as close as it gets.
export function formatDotenvEntry(key: string, value: string): { text: string; exact: boolean } {
    const candidates = value.includes('\n') || value.includes('\r') ? multilineCandidates(value) : singleLineCandidates(value);
    for (const candidate of candidates) {
        const text = `${key}=${candidate}`;
        const parsed = parseDotenv(text);
        if (parsed.errors.length === 0 && parsed.entries.length === 1 && parsed.entries[0]?.value === value) {
            return { text, exact: true };
        }
    }

    return { text: `${key}=${doubleQuoted(value)}`, exact: false };
}

// A whole file: an optional header as comments, then one entry a line.
export function formatDotenv(entries: ReadonlyArray<{ key: string; value: string }>, header?: string): string {
    const head = header
        ? header
              .split('\n')
              .map((text) => `# ${text}`.trimEnd())
              .join('\n') + '\n\n'
        : '';

    return head + entries.map((entry) => formatDotenvEntry(entry.key, entry.value).text).join('\n') + (entries.length > 0 ? '\n' : '');
}

const PLAIN = /^[A-Za-z0-9_@%+=:,./~^-]+$/;

function singleLineCandidates(value: string): string[] {
    return [...(PLAIN.test(value) ? [value] : []), `'${value}'`, `"${value}"`, `\`${value}\``];
}

function multilineCandidates(value: string): string[] {
    return [doubleQuoted(value), `'${value}'`, `\`${value}\``, `"${value}"`];
}

const doubleQuoted = (value: string): string => `"${value.replace(/\r/g, '\\r').replace(/\n/g, '\\n')}"`;

// Where a quoted value ends, as dotenv's pattern finds it: at the first unescaped quote, or, when text follows that
// one on its line, at an escaped quote before it that ends its line. The value may span lines.
function closingQuote(text: string, from: number, quote: string): number | null {
    const candidates: number[] = [];
    for (let i = from; i < text.length; i += 1) {
        if (text[i] === '\\' && text[i + 1] === quote) {
            candidates.push(i + 1);
            i += 1;
        } else if (text[i] === quote) {
            candidates.push(i);
            break;
        }
    }
    for (let index = candidates.length - 1; index >= 0; index -= 1) {
        const at = candidates[index] as number;
        if (BLANK_OR_COMMENT.test(text.slice(at + 1, endOfLine(text, at)))) {
            return at;
        }
    }

    return null;
}

// Unquoted, dotenv reads up to the first #, wherever it is, and trims.
function unquotedPart(raw: string): string {
    const hash = raw.indexOf('#');

    return (hash < 0 ? raw : raw.slice(0, hash)).trim();
}

function endOfLine(text: string, from: number): number {
    const end = text.indexOf('\n', from);

    return end < 0 ? text.length : end;
}

function countLines(text: string, from: number, to: number): number {
    let lines = 0;
    for (let i = from; i < Math.min(to, text.length); i += 1) {
        if (text[i] === '\n') {
            lines += 1;
        }
    }

    return Math.max(1, lines);
}

const quoteName = (quote: string): string => (quote === '"' ? 'double' : quote === "'" ? 'single' : 'back');
